<template>
  <div>
    <EditorFormPanel v-model="textOpen" :title="getEditorTranslation('item-card-label')" data-testid="item-grid-card">
      <div class="py-2">
        <div class="flex items-center justify-between px-2 pb-2 text-sm font-medium text-gray-700">
          <span>{{ getEditorTranslation('item-card-text') }}</span>
          <SfTooltip :label="getEditorTranslation('item-card-tooltip')" placement="top">
            <SfIconInfo size="base" class="ml-2 text-gray-500" />
          </SfTooltip>
        </div>
        <draggable
          v-if="categoryDataBlock.fieldsOrder.length"
          v-model="categoryDataBlock.fieldsOrder"
          item-key="meta.uuid"
          handle=".drag-slides-handle"
          class="rounded"
          :filter="'.no-drag'"
        >
          <template #item="{ element: elem, index }: { element: CategoryDataFieldKey; index: number }">
            <div :key="elem" class="flex items-center justify-between drag-slides-handle cursor-move">
              <div class="flex items-center gap-3">
                <button
                  class="drag-slides-handle top-2 left-2 z-raised cursor-grab p-2 hover:bg-gray-100 rounded-full"
                  :aria-label="getEditorTranslation('drag-reorder-aria')"
                  :data-testid="`actions-drag-slide-handle-${index}`"
                >
                  <NuxtImg width="18" height="18" :src="dragIcon" />
                </button>

                <span>{{ fieldLabels[elem] }}</span>
              </div>
              <SfSwitch
                v-model="categoryDataBlock.fields[elem]"
                :disabled="categoryDataBlock.fieldsDisabled?.includes(elem)"
                :data-testid="`item-grid-field-${elem}`"
              />
            </div>
          </template>
        </draggable>
        <div
          v-if="showTextHint"
          class="mx-4 mt-4 mb-4 flex items-start gap-2 text-sm text-neutral-600"
          role="alert"
          aria-live="polite"
          data-testid="fields-empty-hint"
        >
          <SfIconWarning class="mt-0.5 shrink-0 text-yellow-500" aria-hidden="true" />
          <span class="italic">
            {{ fieldsEmptyHintText }}
            (
            <a :href="learnMoreTextUrl" target="_blank" rel="noopener noreferrer" class="underline">
              {{ getEditorTranslation('learn-more') }}
            </a>
            ).
          </span>
        </div>
      </div>
    </EditorFormPanel>

    <EditorFormPanel v-model="imageOpen" :title="getEditorTranslation('image-label')" data-testid="category-data-image">
      <div class="py-2">
        <EditorOptionsTabs
          v-model="displayCategoryImageModel"
          :legend="getEditorTranslation('display-category-image-label')"
          test-id-prefix="display-category-image"
          :options="displayCategoryImageOptions"
        />
      </div>
      <div
        v-if="showImageSlotHint"
        class="mx-4 mt-4 mb-4 flex items-start gap-2 text-sm text-neutral-600"
        role="alert"
        aria-live="polite"
        data-testid="image-slot-empty-hint"
      >
        <SfIconWarning class="mt-0.5 shrink-0 text-yellow-500" aria-hidden="true" />
        <span class="italic">
          {{ getEditorTranslation('image-slot-empty-hint-prefix') }}
          <a :href="learnMoreUrl" target="_blank" rel="noopener noreferrer" class="underline">
            {{ getEditorTranslation('learn-more') }} </a
          >.
        </span>
      </div>
      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <EditorOptionsTabs
          v-model="fillModeModel"
          :legend="getEditorTranslation('image-scalling-label')"
          test-id-prefix="image-scaling"
          :options="fillModeOptions"
        />
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <label class="block text-sm font-medium mb-4">{{ getEditorTranslation('image-brightness-label') }}</label>
        <div class="flex items-center gap-4">
          <div class="flex-1 space-y-1">
            <div class="flex justify-between text-xs text-gray-500">
              <span>0%</span>
              <span>100%</span>
            </div>
            <input
              v-model.number="categoryDataBlock.image.brightness"
              type="range"
              min="0"
              max="1"
              step="0.01"
              class="w-full"
            />
          </div>

          <div class="relative">
            <input
              v-model.number="categoryDataBlock.image.brightness"
              type="number"
              min="0"
              max="1"
              class="w-20 px-2 py-1 border rounded text-color-red-500"
              @input="clampBrightness($event, 'image')"
            />
          </div>
        </div>
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <UiFormLabel class="mb-1">{{ getEditorTranslation('image-alt-label') }}</UiFormLabel>
        <SfInput v-model="categoryDataBlock.image.alt" name="alt" type="text" data-testid="slide-alt-text" />
        <div class="typography-text-xs text-gray-500 flex gap-1 mt-2 sm:mb-0">
          {{ getEditorTranslation('image-alt-hint') }}
        </div>
      </div>

      <div class="py-2">
        <UiFormLabel class="mb-1">{{ getEditorTranslation('text-color-label') }}</UiFormLabel>
        <EditorColorPicker v-model="categoryDataBlock.text.color" class="w-full">
          <template #trigger="{ color, toggle }">
            <SfInput v-model="categoryDataBlock.text.color" type="text">
              <template #suffix>
                <button
                  type="button"
                  class="border border-[#a0a0a0] rounded-lg cursor-pointer w-10 h-8"
                  :style="{ backgroundColor: color }"
                  @mousedown.stop
                  @click.stop="toggle"
                />
              </template>
            </SfInput>
          </template>
        </EditorColorPicker>
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <UiFormLabel class="mb-1">{{ getEditorTranslation('textbox-background-label') }}</UiFormLabel>
        <SfSwitch
          v-model="categoryDataBlock.text.background"
          class="checked:bg-editor-button checked:before:hover:bg-editor-button checked:border-gray-500 checked:hover:border:bg-gray-700 hover:border-gray-700 hover:before:bg-gray-700 checked:hover:bg-gray-300 checked:hover:border-gray-400"
        />
      </div>

      <div v-if="categoryDataBlock.text.background" class="py-2">
        <UiFormLabel class="mb-1">{{ getEditorTranslation('textbox-color-label') }}</UiFormLabel>
        <EditorColorPicker v-model="categoryDataBlock.text.bgColor" class="w-full">
          <template #trigger="{ color, toggle }">
            <SfInput v-model="categoryDataBlock.text.bgColor" type="text">
              <template #suffix>
                <button
                  type="button"
                  class="border border-[#a0a0a0] rounded-lg cursor-pointer w-10 h-8"
                  :style="{ backgroundColor: color }"
                  @mousedown.stop
                  @click.stop="toggle"
                />
              </template>
            </SfInput>
          </template>
        </EditorColorPicker>
      </div>

      <div v-if="categoryDataBlock.text.background && categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <label class="block text-sm font-medium mb-4">{{ getEditorTranslation('textbox-opacity-label') }}</label>
        <div class="flex items-center gap-4">
          <div class="flex-1 space-y-1">
            <div class="flex justify-between text-xs text-gray-500">
              <span>0%</span>
              <span>100%</span>
            </div>
            <input
              v-model.number="categoryDataBlock.text.bgOpacity"
              type="range"
              min="0"
              max="1"
              step="0.01"
              class="w-full"
            />
          </div>

          <div class="relative">
            <input
              v-model.number="categoryDataBlock.text.bgOpacity"
              type="number"
              min="0"
              max="1"
              class="w-20 px-2 py-1 border rounded text-color-red-500"
              @input="clampBrightness($event, 'text')"
            />
          </div>
        </div>
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <EditorOptionsTabs
          v-if="categoryDataBlock.displayCategoryImage !== 'off'"
          v-model="textboxAlignXModel"
          :legend="getEditorTranslation('textbox-align-main-label')"
          test-id-prefix="slider-textbox-align-x"
          :options="textboxAlignXOptions"
        />
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <EditorOptionsTabs
          v-if="categoryDataBlock.displayCategoryImage !== 'off'"
          v-model="textboxAlignYModel"
          :legend="getEditorTranslation('textbox-align-cross-label')"
          test-id-prefix="slider-textbox-align-y"
          :options="textboxAlignYOptions"
        />
      </div>

      <div v-if="categoryDataBlock.displayCategoryImage !== 'off'" class="py-2">
        <EditorOptionsTabs
          v-if="categoryDataBlock.displayCategoryImage !== 'off'"
          v-model="textAlignModel"
          :legend="getEditorTranslation('text-align-label')"
          test-id-prefix="slider-text-align"
          :options="textAlignOptions"
        />
      </div>
    </EditorFormPanel>
    <EditorFormPanel v-model="layoutOpen" :title="getEditorTranslation('layout-label')" data-testid="item-grid-card">
      <EditorFullWidthToggle v-model="isFullWidth" :block-uuid="blockUuid" />
      <div
        class="py-2"
        :class="
          categoryDataBlock.image.fillMode !== 'fit' && categoryDataBlock.displayCategoryImage !== 'off'
            ? 'opacity-60  cursor-not-allowed'
            : ''
        "
      >
        <UiFormLabel>{{ getEditorTranslation('padding-label') }}</UiFormLabel>
        <div class="grid grid-cols-4 gap-px rounded-md overflow-hidden border border-gray-300">
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowUpward /></span>
            <input
              v-model.number="categoryDataBlock.layout.paddingTop"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-top"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowDownward /></span>
            <input
              v-model.number="categoryDataBlock.layout.paddingBottom"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-bottom"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowBack /></span>
            <input
              v-model.number="categoryDataBlock.layout.paddingLeft"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-left"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white">
            <span><SfIconArrowForward /></span>
            <input
              v-model.number="categoryDataBlock.layout.paddingRight"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-right"
            />
          </div>
        </div>
      </div>
    </EditorFormPanel>
  </div>
</template>

<script setup lang="ts">
import type { CategoryDataFieldKey } from './types';
import {
  SfIconArrowBack,
  SfIconArrowDownward,
  SfIconArrowForward,
  SfIconArrowUpward,
  SfSwitch,
  SfInput,
  SfTooltip,
  SfIconInfo,
  SfIconWarning,
} from '@storefront-ui/vue';
import dragIcon from '~/assets/icons/paths/drag.svg';
import draggable from 'vuedraggable/src/vuedraggable';

const layoutOpen = ref(true);
const textOpen = ref(true);
const imageOpen = ref(true);

const {
  learnMoreUrl,
  learnMoreTextUrl,
  categoryDataBlock,
  fieldLabels,
  showImageSlotHint,
  showTextHint,
  fieldsEmptyHintText,
  clampBrightness,
} = useCategoryData();

const { blockUuid } = useSiteConfiguration();
const { isFullWidth } = useFullWidthToggleForContent(categoryDataBlock);
const {
  textboxAlignXModel,
  textboxAlignXOptions,
  textboxAlignYModel,
  textboxAlignYOptions,
  fillModeModel,
  fillModeOptions,
  displayCategoryImageModel,
  displayCategoryImageOptions,
  textAlignModel,
  textAlignOptions,
} = useEditorOptionsTabs(() => categoryDataBlock.value as EditorTarget, getEditorTranslation);
</script>

<i18n lang="json">
{
  "en": {
    "layout-label": "Layout",

    "item-card-label": "Category text",
    "item-card-text": "Text display and order",
    "item-card-tooltip": "You can manage the descriptions of categories inside the PlentyONE Backend UI found under Item » Category.",
    "category-placeholder": "Category name",
    "category-name": "Category name",
    "category-description-1": "Category description 1",
    "category-description-2": "Category description 2",
    "short-description": "Short description",
    "drag-reorder-aria": "Drag to reorder",
    "padding-label": "Padding",

    "image-label": "Image",
    "display-category-image-label": "Display category image",
    "off": "Off",
    "image-1": "Image 1",
    "image-2": "Image 2",

    "image-scalling-label": "Image Scaling",
    "image-scalling-tooltip": "Fit: The image maintains its original aspect ratio and fits inside the available space, allowing padding. Fill: The image completely fills the available space, potentially cropping parts of the image, and ignores padding.",
    "image-scalling-fit-label": "Fit",
    "image-scalling-fill-label": "Fill",

    "image-brightness-label": "Brightness",
    "image-alt-label": "Alt",
    "image-alt-hint": "Alternative image text",

    "pretitle-label": "Pre-title",
    "pretitle-placeholder": "PreTitle",
    "main-title-label": "Main title",
    "main-title-placeholder": "Title",
    "subtitle-label": "Subtitle",
    "subtitle-placeholder": "SubTitle",
    "description-label": "Description",
    "description-placeholder": "Text that supports HTML formatting",

    "text-color-label": "Text Colour",
    "textbox-background-label": "Textbox Background",
    "textbox-color-label": "Textbox Colour",
    "textbox-opacity-label": "Textbox Opacity",

    "textbox-align-main-label": "Textbox Alignment (main axis)",
    "textbox-align-main-top-label": "Top",
    "textbox-align-main-center-label": "Center",
    "textbox-align-main-bottom-label": "Bottom",

    "textbox-align-cross-label": "Textbox Alignment (cross axis)",
    "textbox-align-cross-left-label": "Left",
    "textbox-align-cross-center-label": "Center",
    "textbox-align-cross-right-label": "Right",

    "text-align-label": "Text Alignment (x)",
    "text-align-option-left-label": "Left",
    "text-align-option-center-label": "Center",
    "text-align-option-right-label": "Right",

    "field-empty-hint-prefix": "The field {field} has no content",
    "fields-empty-hint-prefix": "The fields {fields} have no content",
    "image-slot-empty-hint-prefix": "The selected image slot doesn't have a linked image",
    "learn-more": "learn more"
  },
  "de": {
    "layout-label": "Layout",

    "item-card-label": "Category text",
    "item-card-text": "Text display and order",
    "item-card-tooltip": "You can manage the descriptions of categories inside the PlentyONE Backend UI found under Item » Category.",
    "category-placeholder": "Category name",
    "category-name": "Category name",
    "category-description-1": "Category description 1",
    "category-description-2": "Category description 2",
    "short-description": "Short description",
    "drag-reorder-aria": "Drag to reorder",
    "padding-label": "Padding",

    "image-label": "Image",
    "display-category-image-label": "Display category image",
    "off": "Off",
    "image-1": "Image 1",
    "image-2": "Image 2",

    "image-scalling-label": "Image Scaling",
    "image-scalling-tooltip": "Fit: The image maintains its original aspect ratio and fits inside the available space, allowing padding. Fill: The image completely fills the available space, potentially cropping parts of the image, and ignores padding.",
    "image-scalling-fit-label": "Fit",
    "image-scalling-fill-label": "Fill",

    "image-brightness-label": "Brightness",
    "image-alt-label": "Alt",
    "image-alt-hint": "Alternative image text",

    "pretitle-label": "Pre-title",
    "pretitle-placeholder": "PreTitle",
    "main-title-label": "Main title",
    "main-title-placeholder": "Title",
    "subtitle-label": "Subtitle",
    "subtitle-placeholder": "SubTitle",
    "description-label": "Description",
    "description-placeholder": "Text that supports HTML formatting",

    "text-color-label": "Text Colour",
    "textbox-background-label": "Textbox Background",
    "textbox-color-label": "Textbox Colour",
    "textbox-opacity-label": "Textbox Opacity",

    "textbox-align-main-label": "Textbox Alignment (main axis)",
    "textbox-align-main-top-label": "Top",
    "textbox-align-main-center-label": "Center",
    "textbox-align-main-bottom-label": "Bottom",

    "textbox-align-cross-label": "Textbox Alignment (cross axis)",
    "textbox-align-cross-left-label": "Left",
    "textbox-align-cross-center-label": "Center",
    "textbox-align-cross-right-label": "Right",

    "text-align-label": "Text Alignment (x)",
    "text-align-option-left-label": "Left",
    "text-align-option-center-label": "Center",
    "text-align-option-right-label": "Right",

    "field-empty-hint-prefix": "The field {field} has no content",
    "fields-empty-hint-prefix": "The fields {fields} have no content",
    "image-slot-empty-hint-prefix": "The selected image slot doesn't have a linked image",
    "learn-more": "learn more"
  }
}
</i18n>
